import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import DialogActions from "@mui/joy/DialogActions";
import DialogContent from "@mui/joy/DialogContent";
import DialogTitle from "@mui/joy/DialogTitle";
import Divider from "@mui/joy/Divider";
import LinearProgress from "@mui/joy/LinearProgress";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalOverflow from "@mui/joy/ModalOverflow";
import Stack from "@mui/joy/Stack";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import TabPanel from "@mui/joy/TabPanel";
import Tabs from "@mui/joy/Tabs";
import Typography from "@mui/joy/Typography";
import React from "react";
import { Permission, PutUserRequest, User, getUserPermissions } from "../../../models/auth";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import AlertDialogModal from "../../../shared/components/ui/AlertDialogModal";
import TitleStylizedUnderline from "../../../shared/components/ui/TitleStylizedUnderline";
import {
  AdminQuartzClientContext,
  CohortQuartzClientContext,
  NoteQuartzClientContext,
} from "../../../shared/services/api/contexts";
import CreateUserForm from "../components/CreateUserForm";
import EditPasswordForm from "../components/EditPasswordForm";
import EditUserForm from "../components/EditUserForm";
import ListTasks from "../components/ListTasks";
import UsersTable from "../components/UsersTable";

// New reusable progress indicator component.
interface ProgressIndicatorProps {
  progress: number;
}
const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ progress }) => (
  <Box
    sx={{
      bgcolor: "white",
      width: "100%",
      mt: 2,
    }}
  >
    <LinearProgress
      determinate
      variant="outlined"
      color="neutral"
      size="sm"
      thickness={32}
      value={progress}
      sx={{
        "--LinearProgress-radius": "0px",
        "--LinearProgress-progressThickness": "24px",
        boxShadow: "sm",
        borderColor: "neutral.500",
      }}
    >
      <Typography
        level="body-xs"
        fontWeight="xl"
        textColor="common.white"
        sx={{ mixBlendMode: "difference" }}
      >
        LOADING… {`${Math.round(progress)}%`}
      </Typography>
    </LinearProgress>
  </Box>
);

export default function AdminPage() {
  // Contexts
  const { showSnackbar } = useSnackbar();
  const adminClient = React.useContext(AdminQuartzClientContext);
  const noteClient = React.useContext(NoteQuartzClientContext);
  const cohortClient = React.useContext(CohortQuartzClientContext);

  // States
  const [userChange, setUserChange] = React.useState(0);
  const [users, setUsers] = React.useState<User[]>([]);
  const [form, setForm] = React.useState<PutUserRequest>({
    credentials: {
      user: "",
      password: "",
    },
    attributes: {
      given_name: "",
      name: "",
      organization: "",
      permissions: [Permission.PATIENT, Permission.DOCUMENT, Permission.COHORT, Permission.PROJECT],
    },
  });
  const [createDialogOpen, setCreateDialogOpen] = React.useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState<boolean>(false);
  const [editPasswordDialogOpen, setEditPasswordDialogOpen] = React.useState<boolean>(false);
  const [deleteUserDialog, setDeleteUserDialog] = React.useState<boolean>(false);
  const [replayNonValidatedNotesDialog, setReplayNonValidatedNotesDialog] =
    React.useState<boolean>(false);
  const [fullRefreshOmop, setFullRefreshOmop] = React.useState<boolean>(false);
  const [progress, setProgress] = React.useState<number>(0);
  const [linearProgress, setLinearProgress] = React.useState<boolean>(false);
  const [deletingUsername, setDeletingUsername] = React.useState<string | null>(null);
  const [isExporting, setIsExporting] = React.useState<boolean>(false);
  const [taskRefreshKey, setTaskRefreshKey] = React.useState<number>(0);

  // Simulate progress updates when a long running task is active.
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (linearProgress) {
      interval = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 5 : prev));
      }, 500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [linearProgress]);

  // Handlers for user CRUD operations
  const handleCreateSubmit = async () => {
    try {
      await adminClient.createUser(form);
      setCreateDialogOpen(false);
      setUserChange((prev) => prev + 1);
    } catch (e: any) {
      console.error(e.message);
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const handleEditSubmit = async () => {
    try {
      await adminClient.updateUser(form.credentials.user, form.attributes);
      setEditDialogOpen(false);
      setUserChange((prev) => prev + 1);
    } catch (e: any) {
      console.error(e.message);
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const handleEditPasswordSubmit = async () => {
    try {
      await adminClient.updateUserPassword(form.credentials.user, form.credentials);
      setEditPasswordDialogOpen(false);
      setUserChange((prev) => prev + 1);
    } catch (e: any) {
      console.error(e.message);
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const deleteUser = async (username: string) => {
    setDeletingUsername(username);
    setDeleteUserDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!deletingUsername) return;
    try {
      await adminClient.deleteUser(deletingUsername);
      setDeleteUserDialog(false);
      setDeletingUsername(null);
      setUserChange((prev) => prev + 1);
    } catch (e: any) {
      console.error(e.message);
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const editUserAttributes = async (user: User) => {
    setForm({
      credentials: { user: user.email, password: "" },
      attributes: {
        ...user.attributes,
        permissions: getUserPermissions(user.attributes),
      },
    });
    setEditDialogOpen(true);
  };

  const editUserPwd = async (user: User) => {
    setForm({ credentials: { user: user.email, password: "" }, attributes: user.attributes });
    setEditPasswordDialogOpen(true);
  };

  const createUser = async () => {
    setForm({
      credentials: {
        user: "",
        password: "",
      },
      attributes: {
        given_name: "",
        name: "",
        organization: "",
        permissions: [
          Permission.PATIENT,
          Permission.DOCUMENT,
          Permission.COHORT,
          Permission.PROJECT,
        ],
      },
    });
    setCreateDialogOpen(true);
  };

  // Handlers for Danger Zone tasks

  // Replay non validated notes
  const handleReplayNonValidatedNotes = async () => {
    setReplayNonValidatedNotesDialog(false);
    setLinearProgress(true);
    setProgress(0);
    try {
      await noteClient.runNLPBatch();
    } catch (e: any) {
      console.error(e.message);
      showSnackbar({ text: e.message, color: "danger" });
    } finally {
      setProgress(100);
      setLinearProgress(false);
    }
  };

  // Launch full refresh of the OMOP database
  const handleFullRefreshOmop = async () => {
    setFullRefreshOmop(false);
    setLinearProgress(true);
    setProgress(0);
    try {
      // Assuming the adminClient has this method.
      await cohortClient.fullRefreshOmopData();
    } catch (e: any) {
      console.error(e.message);
      showSnackbar({ text: e.message, color: "danger" });
    } finally {
      setProgress(100);
      setLinearProgress(false);
    }
  };

  // Export data as ZIP
  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const task = await adminClient.startDataExport();
      setTaskRefreshKey((prev) => prev + 1);
      showSnackbar({
        text: `Export task started (ID: ${task.task_id}). Check the Tasks list to download once ready.`,
        color: "neutral",
      });
    } catch (e: any) {
      console.error(e);
      showSnackbar({
        text: e?.message ?? "Unable to start data export",
        color: "danger",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch users on mount or when userChange updates
  React.useEffect(() => {
    async function fetchUsers() {
      try {
        const resp = await adminClient.listUsers();
        setUsers(resp);
      } catch (e: any) {
        if (e.name === "ForbiddenError") {
          showSnackbar({ text: "You do not have access", color: "danger" });
        } else {
          showSnackbar({ text: e.message, color: "danger" });
        }
      }
    }
    fetchUsers();
  }, [adminClient, userChange, showSnackbar]);

  return (
    <Box sx={{ p: 4 }}>
      <Stack
        sx={{ width: "100%", mb: 4 }}
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        <Typography level="h2" sx={{ display: "inline-block" }}>
          <TitleStylizedUnderline>Admin panel</TitleStylizedUnderline>
        </Typography>
      </Stack>
      <Tabs
        defaultValue={0}
        sx={{
          bgcolor: "transparent",
        }}
      >
        <TabList
          tabFlex={1}
          size="sm"
          sx={{
            justifyContent: "left",
            [`&& .${tabClasses.root}`]: {
              fontWeight: "600",
              flex: "initial",
              color: "text.tertiary",
              [`&.${tabClasses.selected}`]: {
                bgcolor: "transparent",
                color: "text.primary",
                "&::after": {
                  height: "2px",
                  bgcolor: "primary.500",
                },
              },
            },
          }}
        >
          <Tab sx={{ borderRadius: "6px 6px 0 0" }} value={0}>
            <ListItemDecorator>
              <GroupIcon fontSize="small" />
            </ListItemDecorator>
            Users
          </Tab>
          <Tab
            sx={{ borderRadius: "6px 6px 0 0", mr: 1, ml: 1 }}
            indicatorInset
            color="danger"
            value={4}
          >
            <ListItemDecorator>
              <WarningRoundedIcon fontSize="small" />
            </ListItemDecorator>
            Danger zone
          </Tab>
        </TabList>
        <TabPanel value={0}>
          <Button startDecorator={<AddIcon />} onClick={createUser} sx={{ mb: 1 }}>
            Add a user
          </Button>
          <UsersTable
            users={users}
            editUserAttributes={editUserAttributes}
            editUserPwd={editUserPwd}
            deleteUser={deleteUser}
          />
        </TabPanel>
        <TabPanel value={4}>
          <Button color="danger" onClick={() => setReplayNonValidatedNotesDialog(true)}>
            Replay all non validated notes
          </Button>
          <Button color="danger" onClick={() => setFullRefreshOmop(true)} sx={{ ml: 1 }}>
            Launch full refresh OMOP database
          </Button>
          <Button
            color="primary"
            onClick={handleExportData}
            sx={{ ml: 1 }}
            loading={isExporting}
            disabled={isExporting}
          >
            {isExporting ? "Preparing export..." : "Export data"}
          </Button>
          <ListTasks refreshKey={taskRefreshKey} />
          {linearProgress && <ProgressIndicator progress={progress} />}
        </TabPanel>
      </Tabs>

      {/* Alert dialog for replay non validated notes */}
      <AlertDialogModal
        alertMessage="Are you sure you want to replay all non validated notes? It might take a while."
        open={replayNonValidatedNotesDialog}
        setOpen={setReplayNonValidatedNotesDialog}
        handleDelete={handleReplayNonValidatedNotes}
      />

      {/* Alert dialog for full refresh OMOP database */}
      <AlertDialogModal
        alertMessage="Are you sure you want to launch a full refresh of the OMOP database? It might take a while."
        open={fullRefreshOmop}
        setOpen={setFullRefreshOmop}
        handleDelete={handleFullRefreshOmop}
      />

      {/* Modal for creating a new user */}
      <Modal open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <ModalOverflow>
          <ModalDialog sx={{ width: "500px" }}>
            <ModalClose />
            <DialogTitle>Create new user</DialogTitle>
            <CreateUserForm form={form} setForm={setForm} handleCreateSubmit={handleCreateSubmit} />
          </ModalDialog>
        </ModalOverflow>
      </Modal>

      {/* Modal for editing user attributes */}
      <Modal open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <ModalOverflow>
          <ModalDialog sx={{ width: "500px" }}>
            <ModalClose />
            <DialogTitle>Edit {form.credentials.user}</DialogTitle>
            <EditUserForm form={form} setForm={setForm} handleEditSubmit={handleEditSubmit} />
          </ModalDialog>
        </ModalOverflow>
      </Modal>

      {/* Modal for editing user password */}
      <Modal open={editPasswordDialogOpen} onClose={() => setEditPasswordDialogOpen(false)}>
        <ModalDialog>
          <DialogTitle>Edit password for {form.credentials.user}</DialogTitle>
          <EditPasswordForm
            form={form}
            setForm={setForm}
            handleEditPasswordSubmit={handleEditPasswordSubmit}
          />
        </ModalDialog>
      </Modal>

      {/* Modal for confirming user deletion */}
      <Modal open={deleteUserDialog} onClose={() => setDeleteUserDialog(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            Confirmation
          </DialogTitle>
          <Divider />
          <DialogContent>
            Are you sure you want to delete {deletingUsername}? All the user's data will be lost in
            3 weeks.
          </DialogContent>
          <DialogActions>
            <Button variant="solid" color="danger" onClick={handleDeleteUser}>
              Delete user
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => {
                setDeleteUserDialog(false);
                setDeletingUsername(null);
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
